import * as React from "react";
import useEmblaCarousel, {
  type UseEmblaCarouselType,
} from "embla-carousel-react";
import { ChevronRight } from "@/components/icons";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

type CarouselApi = UseEmblaCarouselType[1];
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>;
type CarouselOptions = UseCarouselParameters[0];
type CarouselPlugin = UseCarouselParameters[1];

type CarouselProps = {
  opts?: CarouselOptions;
  plugins?: CarouselPlugin;
  orientation?: "horizontal" | "vertical";
  setApi?: (api: CarouselApi) => void;
};

type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0];
  api: ReturnType<typeof useEmblaCarousel>[1];
  scrollPrev: () => void;
  scrollNext: () => void;
  canScrollPrev: boolean;
  canScrollNext: boolean;
} & CarouselProps;

const CarouselContext = React.createContext<CarouselContextProps | null>(null);

function useCarousel() {
  const context = React.useContext(CarouselContext);

  if (!context) {
    throw new Error("useCarousel must be used within a <Carousel />");
  }

  return context;
}

const Carousel = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & CarouselProps
>(
  (
    {
      orientation = "horizontal",
      opts,
      setApi,
      plugins,
      className,
      children,
      ...props
    },
    ref,
  ) => {
    const defaultOpts: CarouselOptions = React.useMemo(() => ({
      axis: orientation === "horizontal" ? "x" : "y",
      // make swipe less sensitive by increasing drag threshold (px)
      dragThreshold: 30,
      // align slides to the start so next/prev moves a full slide into view
      align: 'start',
      // trim snaps at the end so contain behavior is predictable
      containScroll: 'trimSnaps',
    }), [orientation]);

    const [carouselRef, api] = useEmblaCarousel(
      {
        ...defaultOpts,
        ...opts,
      },
      plugins,
    );
    const [canScrollPrev, setCanScrollPrev] = React.useState(false);
    const [canScrollNext, setCanScrollNext] = React.useState(false);

    const onSelect = React.useCallback((api: CarouselApi) => {
      if (!api) {
        return;
      }

      setCanScrollPrev(api.canScrollPrev());
      setCanScrollNext(api.canScrollNext());
    }, []);

    const scrollPrev = React.useCallback(() => {
      try {
        if (!api) return;
        const current = api.selectedScrollSnap();
        const target = Math.max(0, (current ?? 0) - 1);
        api.scrollTo(target);
      } catch (_) {}
    }, [api]);

    const scrollNext = React.useCallback(() => {
      try {
        if (!api) return;
        const current = api.selectedScrollSnap();
        const snaps = api.scrollSnapList();
        const last = snaps ? snaps.length - 1 : 0;
        const target = Math.min(last, (current ?? 0) + 1);
        api.scrollTo(target);
      } catch (_) {}
    }, [api]);

    const handleKeyDown = React.useCallback(
      (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === "ArrowLeft") {
          event.preventDefault();
          scrollPrev();
        } else if (event.key === "ArrowRight") {
          event.preventDefault();
          scrollNext();
        }
      },
      [scrollPrev, scrollNext],
    );

    React.useEffect(() => {
      if (!api || !setApi) {
        return;
      }

      setApi(api);
    }, [api, setApi]);

    React.useEffect(() => {
      if (!api) {
        return;
      }

      onSelect(api);
      api.on("reInit", onSelect);
      api.on("select", onSelect);

      return () => {
        api?.off("select", onSelect);
      };
    }, [api, onSelect]);

    return (
      <CarouselContext.Provider
        value={{
          carouselRef,
          api: api,
          opts,
          orientation:
            orientation || (opts?.axis === "y" ? "vertical" : "horizontal"),
          scrollPrev,
          scrollNext,
          canScrollPrev,
          canScrollNext,
        }}
      >
        <div
          ref={ref}
          onKeyDownCapture={handleKeyDown}
          className={cn("relative", className)}
          role="region"
          aria-roledescription="carousel"
          {...props}
        >
          {children}
        </div>
      </CarouselContext.Provider>
    );
  },
);
Carousel.displayName = "Carousel";

const CarouselContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const { carouselRef, orientation, api } = useCarousel();
  const viewportRef = React.useRef<HTMLDivElement | null>(null);

  const assignRefs = React.useCallback((el: HTMLDivElement | null) => {
    try {
      // support callback refs and ref objects
      if (typeof carouselRef === "function") {
        try { (carouselRef as any)(el); } catch (_) {}
      } else if (carouselRef && typeof (carouselRef as any).current !== 'undefined') {
        try { (carouselRef as any).current = el; } catch (_) {}
      }
    } catch (_) {}
    viewportRef.current = el;
  }, [carouselRef]);

  // Wheel / trackpad support: horizontal scrolling should navigate carousel
  React.useEffect(() => {
    const node = viewportRef.current;
    if (!node || !api) return;
    let last = 0;
    const onWheel = (e: WheelEvent) => {
      try {
        const now = Date.now();
        // throttle frequent wheel events
        if (now - last < 80) return;
        const rawDx = e.deltaX;
        const dx = Math.abs(rawDx) > 0 ? rawDx : (e.shiftKey ? e.deltaY : 0);
        const dy = e.deltaY;

        // Keep desktop page scroll native unless user intent is clearly horizontal.
        // Shift + wheel is explicit horizontal intent; without shift we require a strong horizontal bias.
        const absDx = Math.abs(dx);
        const absDy = Math.abs(dy);
        const explicitHorizontalIntent = e.shiftKey;
        const strongHorizontalIntent = absDx >= 24 && absDx > absDy * 2;
        if (!explicitHorizontalIntent && !strongHorizontalIntent) return;

        const wantsNext = dx > 0;
        const canMove = wantsNext ? api.canScrollNext() : api.canScrollPrev();
        if (!canMove) return;

        e.preventDefault();
        last = now;
        if (wantsNext) api.scrollNext(); else api.scrollPrev();
      } catch (_) {}
    };
    node.addEventListener('wheel', onWheel, { passive: false });
    return () => node.removeEventListener('wheel', onWheel);
  }, [api]);

  return (
    <div ref={assignRefs} className="overflow-hidden">
      <div
        ref={ref}
        className={cn(
          // use gap instead of negative margins + per-item padding so embla snaps align exactly
          "flex",
          orientation === "horizontal" ? "gap-4" : "flex-col gap-4",
          className,
        )}
        {...props}
      />
    </div>
  );
});
CarouselContent.displayName = "CarouselContent";

const CarouselItem = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const { orientation } = useCarousel();

    return (
    <div
      ref={ref}
      role="group"
      aria-roledescription="slide"
      className={cn(
        // allow slides to size automatically so multiple images can sit side-by-side
        // use sensible min-width breakpoints so items fill the row progressively
        "min-w-[50%] sm:min-w-[33.333%] md:min-w-[25%] shrink-0 grow-0",
        // spacing handled by parent gap; keep vertical spacing for vertical orientation
        orientation === "horizontal" ? "" : "pt-4",
        className,
      )}
      {...props}
    />
  );
});
CarouselItem.displayName = "CarouselItem";

const CarouselPrevious = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<typeof Button>
>(({ className, variant = "default", size = "icon", ...props }, ref) => {
  const { orientation, scrollPrev, canScrollPrev } = useCarousel();

  const hiddenClassPrev = canScrollPrev
    ? "opacity-100 translate-x-0 pointer-events-auto"
    : "opacity-0 -translate-x-2 pointer-events-none";

  return (
    <Button
      ref={ref}
      variant={variant}
      size={size}
      className={cn(
        "absolute z-50 h-8 w-8 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-200 ease-out",
        orientation === "horizontal"
          ? "left-2 top-1/2 -translate-y-1/2"
          : "-top-12 left-1/2 -translate-x-1/2 rotate-90",
        hiddenClassPrev,
        className,
      )}
      onClick={scrollPrev}
      {...props}
      >
      <ChevronRight className="h-3 w-3 sm:h-4 sm:w-4 rotate-180" />
      <span className="sr-only">Previous slide</span>
    </Button>
  );
});
CarouselPrevious.displayName = "CarouselPrevious";

const CarouselNext = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<typeof Button>
>(({ className, variant = "default", size = "icon", ...props }, ref) => {
  const { orientation, scrollNext, canScrollNext } = useCarousel();

  const hiddenClassNext = canScrollNext
    ? "opacity-100 translate-x-0 pointer-events-auto"
    : "opacity-0 translate-x-2 pointer-events-none";

  return (
    <Button
      ref={ref}
      variant={variant}
      size={size}
      className={cn(
        "absolute z-50 h-8 w-8 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-200 ease-out",
        orientation === "horizontal"
          ? "right-2 top-1/2 -translate-y-1/2"
          : "-bottom-12 left-1/2 -translate-x-1/2 rotate-90",
        hiddenClassNext,
        className,
      )}
      onClick={scrollNext}
      {...props}
      >
      <ChevronRight className="h-3 w-3 sm:h-4 sm:w-4" />
      <span className="sr-only">Next slide</span>
    </Button>
  );
});
CarouselNext.displayName = "CarouselNext";

export {
  type CarouselApi,
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
};
